if (!customElements.get("jerseerium-social-showcase")) {
  class JerseeriumSocialShowcase extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll("[data-social-card]"));
      this.viewport = this.querySelector("[data-social-viewport]");
      this.prevButton = this.querySelector("[data-social-prev]");
      this.nextButton = this.querySelector("[data-social-next]");
      this.showControls = this.dataset.showControls === "true";
      this.activeIndex = Math.max(
        0,
        this.cards.findIndex((card) => card.classList.contains("is-active")),
      );
      this.pointerStartX = null;
      this.pointerStartY = null;

      this.onCardClick = this.onCardClick.bind(this);
      this.onCardKeydown = this.onCardKeydown.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.cards.forEach((card) => {
        const media = card.querySelector("[data-card-media]");
        card.posterHTML = media ? media.innerHTML : "";
        card.addEventListener("click", this.onCardClick);
        card.addEventListener("keydown", this.onCardKeydown);
      });

      if (this.prevButton) this.prevButton.addEventListener("click", this.onPrevClick);
      if (this.nextButton) this.nextButton.addEventListener("click", this.onNextClick);
      if (this.viewport) {
        this.viewport.addEventListener("pointerdown", this.onPointerDown);
        this.viewport.addEventListener("pointerup", this.onPointerUp);
        this.viewport.addEventListener("pointercancel", this.onPointerUp);
      }
      document.addEventListener("shopify:block:select", this.onBlockSelect);

      this.select(this.activeIndex, { force: true });
    }

    disconnectedCallback() {
      this.cards.forEach((card) => {
        card.removeEventListener("click", this.onCardClick);
        card.removeEventListener("keydown", this.onCardKeydown);
      });
      if (this.prevButton) this.prevButton.removeEventListener("click", this.onPrevClick);
      if (this.nextButton) this.nextButton.removeEventListener("click", this.onNextClick);
      if (this.viewport) {
        this.viewport.removeEventListener("pointerdown", this.onPointerDown);
        this.viewport.removeEventListener("pointerup", this.onPointerUp);
        this.viewport.removeEventListener("pointercancel", this.onPointerUp);
      }
      document.removeEventListener("shopify:block:select", this.onBlockSelect);
    }

    onCardClick(event) {
      if (event.target.closest("a")) return;

      const card = event.currentTarget;
      const index = this.cards.indexOf(card);
      if (index !== -1 && index !== this.activeIndex) this.select(index);
    }

    onCardKeydown(event) {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (event.target.closest("a")) return;

      event.preventDefault();
      const index = this.cards.indexOf(event.currentTarget);
      if (index !== -1) this.select(index);
    }

    onPrevClick() {
      this.select(this.activeIndex - 1);
    }

    onNextClick() {
      this.select(this.activeIndex + 1);
    }

    onPointerDown(event) {
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
    }

    onPointerUp(event) {
      if (this.pointerStartX === null || this.pointerStartY === null) return;

      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;
      this.pointerStartX = null;
      this.pointerStartY = null;

      if (Math.abs(deltaX) < 42 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      this.select(deltaX < 0 ? this.activeIndex + 1 : this.activeIndex - 1);
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;

      const card = event.target.closest("[data-social-card]");
      const index = this.cards.indexOf(card);
      if (index !== -1) this.select(index);
    }

    select(index, options = {}) {
      const count = this.cards.length;
      if (!count) return;

      const nextIndex = this.wrapIndex(index);
      if (!options.force && nextIndex === this.activeIndex) return;

      this.pauseCurrentVideo();
      this.activeIndex = nextIndex;
      this.updateCardPositions();
      this.renderActiveMedia();
    }

    wrapIndex(index) {
      const count = this.cards.length;
      return ((index % count) + count) % count;
    }

    getCircularOffset(index) {
      const count = this.cards.length;
      let offset = index - this.activeIndex;

      if (count > 2) {
        const half = count / 2;
        if (offset > half) offset -= count;
        if (offset < -half) offset += count;
      }

      return offset;
    }

    updateCardPositions() {
      this.cards.forEach((card, index) => {
        const offset = this.getCircularOffset(index);
        const absOffset = Math.abs(offset);
        const isVisible = absOffset <= 2;
        const isActive = offset === 0;

        card.dataset.position = isVisible ? String(offset) : "hidden";
        card.classList.toggle("is-active", isActive);
        card.classList.toggle("is-near", absOffset === 1);
        card.classList.toggle("is-far", absOffset === 2);
        card.classList.toggle("is-hidden", !isVisible);
        card.setAttribute("aria-current", isActive ? "true" : "false");
        card.setAttribute("tabindex", isVisible ? "0" : "-1");
      });
    }

    renderActiveMedia() {
      this.cards.forEach((card, index) => {
        if (index !== this.activeIndex) this.restorePoster(card);
      });

      const activeCard = this.cards[this.activeIndex];
      const media = activeCard && activeCard.querySelector("[data-card-media]");
      if (!activeCard || !media) return;

      const videoSrc = activeCard.dataset.videoSrc;
      const posterSrc = activeCard.dataset.posterSrc;
      const postUrl = activeCard.dataset.postUrl;
      const platform = activeCard.dataset.platform;
      const title = activeCard.dataset.title;

      if (videoSrc) {
        const video = this.createVideo(videoSrc, posterSrc);
        media.replaceChildren(video);
        this.playVideo(video);
        return;
      }

      const embed = this.getEmbedFromPostUrl(postUrl, platform);
      if (embed) {
        media.replaceChildren(this.createEmbed(embed, title));
        return;
      }

      this.restorePoster(activeCard);
    }

    restorePoster(card) {
      const media = card && card.querySelector("[data-card-media]");
      if (!media || media.innerHTML === card.posterHTML) return;
      media.innerHTML = card.posterHTML;
    }

    createVideo(videoSrc, posterSrc) {
      const video = document.createElement("video");
      video.className = "jfh-social-card__video";
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.preload = "metadata";
      video.controls = this.showControls;
      video.setAttribute("playsinline", "");
      if (posterSrc) video.poster = posterSrc;

      const source = document.createElement("source");
      source.src = videoSrc;
      source.type = "video/mp4";
      video.append(source);

      return video;
    }

    createEmbed(embed, title) {
      const wrapper = document.createElement("div");
      wrapper.className = "jfh-social-card__embed";
      wrapper.dataset.embedType = embed.type;

      const iframe = document.createElement("iframe");
      iframe.src = embed.src;
      iframe.title = title || embed.title;
      iframe.loading = "lazy";
      iframe.allow = "autoplay; encrypted-media; fullscreen; picture-in-picture";
      iframe.setAttribute("allowfullscreen", "");

      wrapper.append(iframe);
      return wrapper;
    }

    getEmbedFromPostUrl(postUrl, platform) {
      if (!postUrl) return null;

      let parsedUrl;
      try {
        parsedUrl = new URL(postUrl, window.location.origin);
      } catch (error) {
        return null;
      }

      const hostname = parsedUrl.hostname.replace(/^www\./, "").toLowerCase();
      const pathname = parsedUrl.pathname;
      const normalizedPlatform = (platform || "").toLowerCase();
      const isTikTok = normalizedPlatform === "tiktok" || hostname.endsWith("tiktok.com");
      const isInstagram = normalizedPlatform === "instagram" || hostname.endsWith("instagram.com");

      if (isTikTok) {
        const videoMatch = pathname.match(/\/video\/(\d+)/);
        if (!videoMatch) return null;

        return {
          type: "tiktok",
          src: `https://www.tiktok.com/embed/v2/${videoMatch[1]}?autoplay=1&muted=1`,
          title: "TikTok video",
        };
      }

      if (isInstagram) {
        const postMatch = pathname.match(/\/(p|reel|tv)\/([^/?#]+)/);
        if (!postMatch) return null;

        return {
          type: "instagram",
          src: `https://www.instagram.com/${postMatch[1]}/${postMatch[2]}/embed/captioned/`,
          title: "Instagram post",
        };
      }

      return null;
    }

    pauseCurrentVideo() {
      const currentVideo = this.querySelector(".jfh-social-card.is-active video");
      if (currentVideo) currentVideo.pause();
    }

    playVideo(video) {
      video.play().catch(() => {
        video.removeAttribute("autoplay");
      });
    }
  }

  customElements.define("jerseerium-social-showcase", JerseeriumSocialShowcase);
}
